import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'react-modal'
import { ROBOT_NETWORK_STATUSES } from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'

import MapRecordingView from './MapRecordingView/MapRecordingView'
import { MappingFormData } from './MapRecordingView/types'

import style from './MapRecordingModal.module.css'

interface IMapRecordingModal {
  isOpen: boolean
  onClose: () => void
  onConfirm: (formData: MappingFormData) => void
  robotInfo?: IRobotInfo
  className?: string
}

const MapRecordingModal: React.FC<IMapRecordingModal> = props => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<MappingFormData>({
    subMode: 'new_location', // Changed from 'update_existing' - that option is temporarily hidden
  })
  const [isValid, setIsValid] = useState(false)

  const onSaveClick = async () => {
    if (!isValid) {
      return
    }
    setSaving(true)
    try {
      await props.onConfirm(formData)
    } finally {
      setSaving(false)
    }
  }

  const onCancelClick = () => {
    props.onClose()
  }

  const handleFormDataChange = useCallback((data: MappingFormData) => {
    setFormData(data)
  }, [])

  const handleValidationChange = useCallback((valid: boolean) => {
    setIsValid(valid)
  }, [])

  const isRobotOffline = props.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.OFFLINE

  return (
    <Modal
      isOpen={props.isOpen}
      onRequestClose={props.onClose}
      className={clsx(style.root, props.className)}
      shouldCloseOnOverlayClick={true}
      style={{ overlay: { zIndex: 100 } }}
      ariaHideApp={false}
    >
      <div className={style.titleBlock}>
        <div className={style.title}>Start Map Recording</div>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {isRobotOffline && (
          <div className={clsx(style.alert, style.alertError)}>
            Error: Robot must be online to start map recording. Please ensure robot is online.
          </div>
        )}
        {props.robotInfo && (
          <MapRecordingView
            robotInfo={props.robotInfo}
            formData={formData}
            onFormDataChange={handleFormDataChange}
            onValidationChange={handleValidationChange}
            isDisabled={isRobotOffline}
          />
        )}
      </div>

      <div className={style.footer}>
        <Button.Outlined className={style.btn} onClick={onCancelClick}>
          {t('common.cancel')}
        </Button.Outlined>
        <Button.Standart
          className={style.btn}
          disabled={saving || !isValid || isRobotOffline}
          loading={saving}
          onClick={onSaveClick}
        >
          Start Recording
        </Button.Standart>
      </div>
    </Modal>
  )
}

export default MapRecordingModal
